import type { InputManager } from '../input/InputManager';
import {
  ACTION_LABELS,
  KEYBOARD_MOVE_ACTIONS,
  REBINDABLE_ACTIONS,
  formatGamepadBinding,
  formatKeyboardBinding,
  formatMouseButton,
  type GameAction,
} from '../input/bindings';
import {
  detectWeakDevice,
  qualityLabel,
  resolveGraphics,
  type GraphicsQuality,
} from '../core/graphicsSettings';

export interface SettingsUIHost {
  input: InputManager;
  requestPointerLock(): void;
  exitPointerLock(): void;
  /** Called when settings close and gameplay should resume (optional). */
  onClosed?: () => void;
  getGraphicsQuality?: () => GraphicsQuality;
  setGraphicsQuality?: (quality: GraphicsQuality) => void;
}

type SettingsTab = 'graphics' | 'keyboard' | 'gamepad';

/**
 * Overlay for remapping keyboard / gamepad controls and tuning look sensitivity.
 */
export class SettingsUI {
  private readonly root: HTMLDivElement;
  private readonly host: SettingsUIHost;
  private open = false;
  private tab: SettingsTab = 'graphics';
  private status = '';

  constructor(parent: HTMLElement, host: SettingsUIHost) {
    this.host = host;
    this.root = document.createElement('div');
    this.root.id = 'settings-overlay';
    this.root.innerHTML = `
      <div class="settings-panel" role="dialog" aria-label="Settings">
        <header class="settings-header">
          <h2>Settings</h2>
          <span class="settings-hint">Graphics · Controls · Esc cancels rebind</span>
        </header>
        <div class="settings-tabs">
          <button type="button" data-tab="graphics">Graphics</button>
          <button type="button" data-tab="keyboard">Keyboard & Mouse</button>
          <button type="button" data-tab="gamepad">Controller</button>
        </div>
        <div class="settings-body"></div>
        <footer class="settings-footer">
          <p class="settings-status"></p>
          <div class="settings-actions">
            <button type="button" data-action="reset" class="secondary">Reset defaults</button>
            <button type="button" data-action="close">Close</button>
          </div>
        </footer>
      </div>
    `;
    parent.appendChild(this.root);
    this.injectStyles();

    this.root.addEventListener('click', (e) => {
      if (e.target === this.root) this.setOpen(false);
    });

    this.root.querySelectorAll('[data-tab]').forEach((el) => {
      el.addEventListener('click', () => {
        this.tab = (el as HTMLElement).dataset.tab as SettingsTab;
        this.render();
      });
    });

    this.root.querySelector('[data-action="close"]')?.addEventListener('click', () => {
      this.setOpen(false);
    });
    this.root.querySelector('[data-action="reset"]')?.addEventListener('click', () => {
      if (this.tab === 'graphics') {
        this.host.setGraphicsQuality?.('auto');
        this.status = 'Graphics set to Auto.';
      } else {
        this.host.input.resetToDefaults();
        this.status = 'Restored default controls.';
      }
      this.render();
    });
  }

  get isOpen(): boolean {
    return this.open;
  }

  toggle(): void {
    this.setOpen(!this.open);
  }

  setOpen(open: boolean): void {
    if (this.open === open) return;
    this.open = open;
    if (open) {
      this.host.exitPointerLock();
      this.host.input.cancelRebind();
      this.status = '';
      this.root.classList.add('open');
      this.render();
    } else {
      this.host.input.cancelRebind();
      this.root.classList.remove('open');
      this.host.onClosed?.();
    }
  }

  dispose(): void {
    this.host.input.cancelRebind();
    this.root.remove();
  }

  private render(): void {
    const body = this.root.querySelector('.settings-body') as HTMLElement;
    const statusEl = this.root.querySelector('.settings-status') as HTMLElement;
    const settings = this.host.input.getSettings();
    const padLabel = this.host.input.getConnectedGamepadLabel();

    this.root.querySelectorAll('[data-tab]').forEach((el) => {
      const btn = el as HTMLElement;
      btn.classList.toggle('active', btn.dataset.tab === this.tab);
    });

    if (this.tab === 'graphics') {
      const quality = this.host.getGraphicsQuality?.() ?? 'auto';
      const resolved = resolveGraphics({ quality });
      const autoHint = detectWeakDevice()
        ? 'Auto picks Low on this device.'
        : 'Auto picks High on this device.';
      body.innerHTML = `
        <p class="settings-note">Lower quality cuts resolution, lights, and draw distance for tablets and phones.</p>
        <p class="settings-note">${escapeHtml(autoHint)} Currently running as <strong>${qualityLabel(resolved.tier)}</strong>.</p>
        <div class="settings-quality" role="group" aria-label="Graphics quality">
          ${qualityButton('auto', quality)}
          ${qualityButton('low', quality)}
          ${qualityButton('medium', quality)}
          ${qualityButton('high', quality)}
        </div>
        <ul class="settings-gfx-list">
          <li>Render scale ≈ ${Math.round(resolved.renderScale * resolved.pixelRatioCap * 100)}% of display DPR</li>
          <li>Fog distance ${resolved.fogFar}m · minimap ${resolved.minimapHz} Hz</li>
          <li>Portal lights ${resolved.portalLights ? 'on' : 'off'} · rim light ${resolved.rimLight ? 'on' : 'off'}</li>
        </ul>
      `;
    } else if (this.tab === 'keyboard') {
      const rows = REBINDABLE_ACTIONS.map((action) => {
        const binding =
          action === 'primary'
            ? `${formatMouseButton(settings.mouse.primaryButton)}${
                settings.keyboard.primary.length
                  ? ` · ${formatKeyboardBinding(settings.keyboard.primary)}`
                  : ''
              }`
            : formatKeyboardBinding(settings.keyboard[action]);
        return bindRow(action, binding, 'keyboard');
      }).join('');

      body.innerHTML = `
        <div class="settings-sliders">
          <label>Mouse sensitivity
            <input type="range" min="4" max="80" step="1" data-mouse-sens
              value="${Math.round(settings.mouse.sensitivity * 10000)}" />
          </label>
          <label class="settings-check">
            <input type="checkbox" data-mouse-invert ${settings.mouse.invertY ? 'checked' : ''} />
            Invert mouse Y
          </label>
        </div>
        <div class="settings-list">${rows}</div>
      `;
    } else {
      const rows = REBINDABLE_ACTIONS.filter((a) => !KEYBOARD_MOVE_ACTIONS.includes(a))
        .map((action) =>
          bindRow(action, formatGamepadBinding(settings.gamepad.buttons[action]), 'gamepad'),
        )
        .join('');

      body.innerHTML = `
        <p class="settings-pad">${
          padLabel
            ? `Connected: ${escapeHtml(shortPadName(padLabel))}`
            : 'No controller detected — press any button to connect.'
        }</p>
        <p class="settings-note">Move: left stick · Look: right stick</p>
        <div class="settings-sliders">
          <label>Look sensitivity
            <input type="range" min="4" max="80" step="1" data-pad-sens
              value="${Math.round(settings.gamepad.lookSensitivity * 10)}" />
          </label>
          <label>Stick deadzone
            <input type="range" min="5" max="40" step="1" data-pad-dz
              value="${Math.round(settings.gamepad.moveDeadzone * 100)}" />
          </label>
          <label class="settings-check">
            <input type="checkbox" data-pad-invert ${settings.gamepad.invertY ? 'checked' : ''} />
            Invert look Y
          </label>
        </div>
        <div class="settings-list">${rows}</div>
      `;
    }

    statusEl.textContent = this.status;
    this.bindBody();
  }

  private bindBody(): void {
    const body = this.root.querySelector('.settings-body') as HTMLElement;

    body.querySelectorAll('[data-quality]').forEach((el) => {
      el.addEventListener('click', () => {
        const quality = (el as HTMLElement).dataset.quality as GraphicsQuality;
        this.host.setGraphicsQuality?.(quality);
        this.status = `Graphics set to ${qualityLabel(quality)}.`;
        this.render();
      });
    });

    body.querySelectorAll('[data-rebind]').forEach((el) => {
      el.addEventListener('click', () => {
        const action = (el as HTMLElement).dataset.rebind as GameAction;
        const device = (el as HTMLElement).dataset.device as 'keyboard' | 'gamepad';
        void this.startRebind(device, action, el as HTMLElement);
      });
    });

    body.querySelectorAll('[data-clear]').forEach((el) => {
      el.addEventListener('click', (e) => {
        e.stopPropagation();
        const action = (el as HTMLElement).dataset.clear as GameAction;
        const device = (el as HTMLElement).dataset.device as 'keyboard' | 'gamepad';
        if (device === 'keyboard') this.host.input.clearKeyboardBinding(action);
        else this.host.input.clearGamepadBinding(action);
        this.status = `Cleared ${ACTION_LABELS[action]}.`;
        this.render();
      });
    });

    const mouseSens = body.querySelector('[data-mouse-sens]') as HTMLInputElement | null;
    mouseSens?.addEventListener('input', () => {
      this.host.input.setMouseSensitivity(Number(mouseSens.value) / 10000);
    });

    const mouseInvert = body.querySelector('[data-mouse-invert]') as HTMLInputElement | null;
    mouseInvert?.addEventListener('change', () => {
      this.host.input.setMouseInvertY(mouseInvert.checked);
    });

    const padSens = body.querySelector('[data-pad-sens]') as HTMLInputElement | null;
    padSens?.addEventListener('input', () => {
      this.host.input.setGamepadLookSensitivity(Number(padSens.value) / 10);
    });

    const padDz = body.querySelector('[data-pad-dz]') as HTMLInputElement | null;
    padDz?.addEventListener('input', () => {
      const dz = Number(padDz.value) / 100;
      this.host.input.setGamepadDeadzones(dz, Math.max(0.05, dz - 0.02));
    });

    const padInvert = body.querySelector('[data-pad-invert]') as HTMLInputElement | null;
    padInvert?.addEventListener('change', () => {
      this.host.input.setGamepadInvertY(padInvert.checked);
    });
  }

  private async startRebind(
    device: 'keyboard' | 'gamepad',
    action: GameAction,
    el: HTMLElement,
  ): Promise<void> {
    this.status =
      device === 'keyboard'
        ? `Press a key for ${ACTION_LABELS[action]}… (Esc cancel)`
        : `Press a controller button for ${ACTION_LABELS[action]}… (B cancel)`;
    el.classList.add('listening');
    const statusEl = this.root.querySelector('.settings-status') as HTMLElement;
    statusEl.textContent = this.status;

    const ok = await this.host.input.beginRebind(device, action);
    this.status = ok ? `Bound ${ACTION_LABELS[action]}.` : 'Rebind cancelled.';
    this.render();
  }

  private injectStyles(): void {
    if (document.getElementById('settings-ui-styles')) return;
    const style = document.createElement('style');
    style.id = 'settings-ui-styles';
    style.textContent = `
      #settings-overlay {
        position: absolute;
        inset: 0;
        z-index: 55;
        display: none;
        place-items: center;
        pointer-events: none;
        background: rgba(6, 10, 16, 0.72);
        backdrop-filter: blur(3px);
      }
      #settings-overlay.open {
        display: grid;
        pointer-events: auto;
      }
      #settings-overlay .settings-panel {
        width: min(520px, calc(100vw - 28px));
        max-height: min(86vh, 720px);
        display: flex;
        flex-direction: column;
        border: 1px solid rgba(232, 238, 245, 0.14);
        background: rgba(14, 20, 28, 0.94);
        box-shadow: 0 18px 50px rgba(0, 0, 0, 0.5);
      }
      #settings-overlay .settings-header {
        padding: 16px 18px 10px;
        border-bottom: 1px solid rgba(232, 238, 245, 0.08);
      }
      #settings-overlay .settings-header h2 {
        margin: 0 0 4px;
        font-size: 18px;
        letter-spacing: 0.04em;
      }
      #settings-overlay .settings-hint {
        font-size: 11px;
        opacity: 0.55;
      }
      #settings-overlay .settings-tabs {
        display: grid;
        grid-template-columns: 1fr 1fr 1fr;
        gap: 0;
        border-bottom: 1px solid rgba(232, 238, 245, 0.08);
      }
      #settings-overlay .settings-tabs button {
        appearance: none;
        border: 0;
        background: transparent;
        color: rgba(232, 238, 245, 0.65);
        font: inherit;
        font-size: 12px;
        padding: 10px 8px;
        cursor: pointer;
        border-bottom: 2px solid transparent;
      }
      #settings-overlay .settings-tabs button.active {
        color: #e8eef5;
        border-bottom-color: #5ec8ff;
        background: rgba(94, 200, 255, 0.06);
      }
      #settings-overlay .settings-body {
        padding: 12px 16px;
        overflow: auto;
        flex: 1;
      }
      #settings-overlay .settings-sliders {
        display: grid;
        gap: 10px;
        margin-bottom: 14px;
      }
      #settings-overlay .settings-sliders label {
        display: grid;
        gap: 4px;
        font-size: 12px;
        opacity: 0.9;
      }
      #settings-overlay .settings-check {
        display: flex !important;
        align-items: center;
        gap: 8px;
        grid-template-columns: none;
      }
      #settings-overlay input[type="range"] {
        width: 100%;
      }
      #settings-overlay .settings-pad,
      #settings-overlay .settings-note {
        margin: 0 0 8px;
        font-size: 12px;
        opacity: 0.7;
      }
      #settings-overlay .settings-quality {
        display: grid;
        grid-template-columns: repeat(4, 1fr);
        gap: 6px;
        margin: 12px 0 14px;
      }
      #settings-overlay .settings-quality button {
        appearance: none;
        border: 1px solid rgba(232, 238, 245, 0.18);
        background: rgba(0, 0, 0, 0.28);
        color: #e8eef5;
        font: inherit;
        font-size: 12px;
        padding: 10px 6px;
        cursor: pointer;
      }
      #settings-overlay .settings-quality button.active {
        border-color: #5ec8ff;
        background: rgba(94, 200, 255, 0.16);
      }
      #settings-overlay .settings-gfx-list {
        margin: 0;
        padding-left: 18px;
        font-size: 12px;
        opacity: 0.7;
        line-height: 1.55;
      }
      #settings-overlay .settings-list {
        display: grid;
        gap: 4px;
      }
      #settings-overlay .bind-row {
        display: grid;
        grid-template-columns: 1fr auto auto;
        gap: 8px;
        align-items: center;
        padding: 8px 10px;
        background: rgba(0, 0, 0, 0.28);
        border: 1px solid transparent;
        cursor: pointer;
        text-align: left;
        color: inherit;
        font: inherit;
        width: 100%;
      }
      #settings-overlay .bind-row:hover {
        border-color: rgba(94, 200, 255, 0.35);
      }
      #settings-overlay .bind-row.listening {
        border-color: #5ec8ff;
        background: rgba(94, 200, 255, 0.12);
      }
      #settings-overlay .bind-label {
        font-size: 13px;
      }
      #settings-overlay .bind-value {
        font-size: 12px;
        opacity: 0.8;
        font-variant-numeric: tabular-nums;
      }
      #settings-overlay .bind-clear {
        appearance: none;
        border: 0;
        background: transparent;
        color: rgba(232, 238, 245, 0.45);
        font: inherit;
        font-size: 11px;
        cursor: pointer;
        padding: 2px 4px;
      }
      #settings-overlay .bind-clear:hover {
        color: #ffb4a2;
      }
      #settings-overlay .settings-footer {
        padding: 10px 16px 14px;
        border-top: 1px solid rgba(232, 238, 245, 0.08);
        display: grid;
        gap: 8px;
      }
      #settings-overlay .settings-status {
        margin: 0;
        min-height: 1.2em;
        font-size: 12px;
        color: #9ed0ff;
      }
      #settings-overlay .settings-actions {
        display: flex;
        gap: 8px;
        justify-content: flex-end;
      }
      #settings-overlay .settings-actions button {
        appearance: none;
        border: 1px solid rgba(232, 238, 245, 0.22);
        background: #2a4a62;
        color: #e8eef5;
        font: inherit;
        font-size: 13px;
        padding: 8px 14px;
        cursor: pointer;
      }
      #settings-overlay .settings-actions button.secondary {
        background: rgba(42, 74, 98, 0.4);
      }
      #settings-overlay .settings-actions button:hover {
        filter: brightness(1.08);
      }
    `;
    document.head.appendChild(style);
  }
}

function qualityButton(quality: GraphicsQuality, current: GraphicsQuality): string {
  const active = quality === current ? ' active' : '';
  return `<button type="button" class="${active.trim()}" data-quality="${quality}">${qualityLabel(quality)}</button>`;
}

function bindRow(action: GameAction, value: string, device: 'keyboard' | 'gamepad'): string {
  return `
    <div class="bind-row-wrap" style="display:grid;grid-template-columns:1fr auto;gap:4px;align-items:stretch">
      <button type="button" class="bind-row" data-rebind="${action}" data-device="${device}">
        <span class="bind-label">${ACTION_LABELS[action]}</span>
        <span class="bind-value">${escapeHtml(value)}</span>
      </button>
      <button type="button" class="bind-clear" data-clear="${action}" data-device="${device}" title="Clear">✕</button>
    </div>
  `;
}

function shortPadName(id: string): string {
  return id.length > 48 ? `${id.slice(0, 45)}…` : id;
}

function escapeHtml(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}
